import { ThemeType } from "@hex/common";
import { McpUiStyleVariableKey } from "@modelcontextprotocol/ext-apps/react";
import { darken, lighten, rgba } from "polished";
import React from "react";

import { PartialThemeVariables, Theme } from "../theme/common/theme";
import { createDarkMode } from "../theme/themes/darkTheme";
import { createLightMode } from "../theme/themes/lightTheme";

export const McpThemeContext = React.createContext<Theme | undefined>(
  undefined,
);

// If `light-dark` is specified, we need to extract the relevant color based on the theme
export const extractColorForTheme = (
  color: string,
  themeType: ThemeType,
): string => {
  if (color.startsWith("light-dark(")) {
    // Format looks like light-dark(lightColor, darkColor) where lightColor and darkColor are any valid CSS color values which can contain commas in paraentheses
    // Remove 'light-dark(' prefix and ')' suffix
    const content = color.slice(11, -1).trim();

    // Find the separator comma (not commas inside parentheses like rgba())
    let depth = 0;
    let separatorIndex = -1;

    for (let i = 0; i < content.length; i++) {
      const char = content[i];
      if (char === "(") {
        depth++;
      } else if (char === ")") {
        depth--;
      } else if (char === "," && depth === 0) {
        separatorIndex = i;
        break;
      }
    }

    if (separatorIndex === -1) {
      // Fallback if no separator found
      return color;
    }

    const lightColor = content.slice(0, separatorIndex).trim();
    const darkColor = content.slice(separatorIndex + 1).trim();

    return themeType === ThemeType.LIGHT ? lightColor : darkColor;
  }
  return color;
};

const generateOverrideVariables = (
  styleVariables: Record<McpUiStyleVariableKey, string | undefined>,
  themeType: ThemeType,
): PartialThemeVariables => {
  const fontFamilyOverrides: NonNullable<PartialThemeVariables["FontFamily"]> =
    {};
  if (styleVariables["--font-sans"]) {
    fontFamilyOverrides.DEFAULT = styleVariables["--font-sans"];
  }
  if (styleVariables["--font-mono"]) {
    fontFamilyOverrides.MONO = styleVariables["--font-mono"];
  }
  const colorVarOverrides: NonNullable<PartialThemeVariables["ColorVars"]> = {};
  const backgroundColorOverrides: NonNullable<
    PartialThemeVariables["BackgroundColor"]
  > = {};

  if (
    styleVariables["--color-background-primary"] &&
    styleVariables["--color-background-secondary"]
  ) {
    // This is weird, but to match anthropic's background, we need to use the secondary bg color as the background and the primary color as the secondary
    const defaultColor = extractColorForTheme(
      styleVariables["--color-background-primary"],
      themeType,
    );
    const mutedColor = extractColorForTheme(
      styleVariables["--color-background-secondary"],
      themeType,
    );
    backgroundColorOverrides.DEFAULT = defaultColor;
    backgroundColorOverrides.LOGIC = mutedColor;
    backgroundColorOverrides.MUTED = mutedColor;
    backgroundColorOverrides.OVERLAY = rgba(mutedColor, 0.8);
    backgroundColorOverrides.DARK = darken(0.1, defaultColor);
  }

  const fontColorOverrides: NonNullable<PartialThemeVariables["FontColor"]> =
    {};

  if (styleVariables["--color-text-primary"]) {
    fontColorOverrides.DEFAULT = extractColorForTheme(
      styleVariables["--color-text-primary"],
      themeType,
    );
    fontColorOverrides.BRAND = fontColorOverrides.DEFAULT;
  }
  if (styleVariables["--color-text-secondary"]) {
    fontColorOverrides.MUTED = extractColorForTheme(
      styleVariables["--color-text-secondary"],
      themeType,
    );
  }
  if (styleVariables["--color-text-inverse"]) {
    fontColorOverrides.INVERTED = extractColorForTheme(
      styleVariables["--color-text-inverse"],
      themeType,
    );
  }

  const borderColorOverrides: NonNullable<
    PartialThemeVariables["BorderColor"]
  > = {};
  if (styleVariables["--color-border-secondary"]) {
    borderColorOverrides.DEFAULT = extractColorForTheme(
      styleVariables["--color-border-secondary"],
      themeType,
    );
  }
  if (styleVariables["--color-border-tertiary"]) {
    borderColorOverrides.MUTED = extractColorForTheme(
      styleVariables["--color-border-tertiary"],
      themeType,
    );
  }
  if (styleVariables["--color-border-inverse"]) {
    borderColorOverrides.INVERSE = extractColorForTheme(
      styleVariables["--color-border-inverse"],
      themeType,
    );
  }

  // For intent colors, we extract the text color and use that to derive the other colors via rgba
  const intentColorsOverrides: NonNullable<
    PartialThemeVariables["IntentColors"]
  > = {};
  let brandColorOverrides = undefined;

  if (styleVariables["--color-text-primary"]) {
    const primaryColor = extractColorForTheme(
      styleVariables["--color-text-primary"],
      themeType,
    );
    brandColorOverrides = primaryColor;
    intentColorsOverrides.PRIMARY = primaryColor;
  }
  if (styleVariables["--color-text-danger"]) {
    intentColorsOverrides.DANGER = extractColorForTheme(
      styleVariables["--color-text-danger"],
      themeType,
    );
  }
  if (styleVariables["--color-text-success"]) {
    intentColorsOverrides.SUCCESS = extractColorForTheme(
      styleVariables["--color-text-success"],
      themeType,
    );
  }
  if (styleVariables["--color-text-warning"]) {
    intentColorsOverrides.WARNING = extractColorForTheme(
      styleVariables["--color-text-warning"],
      themeType,
    );
  }
  return {
    FontFamily: fontFamilyOverrides,
    ColorVars: colorVarOverrides,
    BrandColor: brandColorOverrides,
    BackgroundColor: backgroundColorOverrides,
    FontColor: fontColorOverrides,
    BorderColor: borderColorOverrides,
    IntentColors: intentColorsOverrides,
  };
};

/**
 * Another kind of hacky way to provide styling; this is because we don't have the full set of color palettes from the host and want to just overwrite certain values
 */
const overwriteVariablesAfterGeneration = (
  theme: Theme,
  styleVariables: Record<McpUiStyleVariableKey, string | undefined> | null,
  themeType: ThemeType,
): Theme => {
  if (styleVariables) {
    if (themeType === ThemeType.LIGHT) {
      if (styleVariables["--color-background-secondary"]) {
        const secondaryBackgroundColor = extractColorForTheme(
          styleVariables["--color-background-secondary"],
          themeType,
        );
        const base = darken(0.15, secondaryBackgroundColor);
        theme.hoverColor = rgba(base, 0.15);
        theme.NonTransparentHoverColor = lighten(0.13, base);
      }
    } else {
      if (styleVariables["--color-background-primary"]) {
        const primaryBackgroundColor = extractColorForTheme(
          styleVariables["--color-background-primary"],
          themeType,
        );
        const base = lighten(0.25, primaryBackgroundColor);
        /* Useful for situations where content may flow behind the hovered object. E.g. sticky tree component header */
        theme.hoverColor = rgba(base, 0.2);
        theme.NonTransparentHoverColor = darken(0.1, base);
      }
    }
  }
  return {
    ...theme,
    isMcpTheme: true,
    isCustomTheme: true,
  };
};

export const createMcpTheme = (
  styleVariables: Record<McpUiStyleVariableKey, string | undefined> | null,
  themeType: ThemeType,
): Theme => {
  const overrides = styleVariables
    ? generateOverrideVariables(styleVariables, themeType)
    : undefined;

  if (themeType === ThemeType.LIGHT) {
    return overwriteVariablesAfterGeneration(
      createLightMode(overrides),
      styleVariables,
      themeType,
    );
  } else {
    return overwriteVariablesAfterGeneration(
      createDarkMode(overrides),
      styleVariables,
      themeType,
    );
  }
};
